// Coldroom -> Temperatures tab (R-A7). The cold store's temperature record,
// PPECB regs 2024 reg 29(1)(e): pick a room, type the air temperature (and the
// pulp temperature if taken), Save. Today's grid shows which check windows have
// a reading and which are missing. A mistake is fixed with Correct: a NEW reading
// that supersedes the old one - nothing is edited or deleted.
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { AlertTriangle, Printer, Snowflake } from 'lucide-react'
import clsx from 'clsx'
import PageHeader from '@/components/PageHeader'
import QueryStateGate from '@/components/QueryStateGate'
import SubmitOverlay from '@/components/SubmitOverlay'
import Modal from '@/components/Modal'
import { Button } from '@/components/ui'
import { supabase } from '@/lib/supabase'
import { fetchAllPaged } from '@/lib/fetchAllPaged'
import { withTimeout } from '@/lib/saveTimeout'
import { sastTodayYMD, sastHM } from '@/lib/dates'
import { readState } from '@/lib/readState'
import { usePermissions } from '@/lib/usePermissions'
import { useMinuteClock } from '@/hooks/useMinuteClock'
import { POLL } from '@/lib/pollTiers'
import {
  buildGrid, fmtTemp, hasRange, parseTemp, rangeStatus, rangeText,
  sanitizeTempInput, sastDayRange, shiftYmd, supersededIds, tempInLimits,
  TEMP_MAX_LIMIT, TEMP_MIN_LIMIT, type GridRow, type TempReading, type TempRoom,
} from '@/lib/coldroomTemps'

const READING_COLS = 'reading_id, coldroom_id, read_at, air_temp_c, pulp_temp_c, note, recorded_by, supersedes_id'
export const TEMP_ROOMS_KEY = ['coldroom-temp-rooms']

/** The room list with each room's range and checks a day. Its own read, so the
 *  Coldroom map never depends on the logbook's columns. */
export async function fetchTempRooms(): Promise<TempRoom[]> {
  const { data, error } = await supabase
    .from('coldrooms')
    .select('coldroom_id, code, name, is_active, temp_min_c, temp_max_c, readings_per_day')
    .order('sort_order').order('code')
  if (error) throw error
  return (data ?? []) as TempRoom[]
}

/** Every reading in [from, to) - paged, so a long range is never cut at 1,000. */
export async function fetchTempReadings(from: string, to: string, roomId?: string): Promise<TempReading[]> {
  const { rows, error } = await fetchAllPaged<TempReading>((a, b) => {
    let q = supabase.from('coldroom_temp_readings').select(READING_COLS)
      .gte('read_at', from).lt('read_at', to)
    if (roomId) q = q.eq('coldroom_id', roomId)
    return q.order('read_at').order('reading_id').range(a, b)
  })
  if (error) throw new Error(error)
  return rows
}

/** Names for the "by" column. Best effort: a failed read only drops the names. */
export function useRecorderNames(rows: { recorded_by: string }[]): Map<string, string> {
  const ids = useMemo(() => [...new Set(rows.map(r => r.recorded_by))].sort(), [rows])
  const q = useQuery({
    queryKey: ['coldroom-temp-recorders', ids],
    enabled: ids.length > 0,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('users').select('id, name').in('id', ids)
      if (error) throw error
      return (data ?? []) as { id: string; name: string | null }[]
    },
  })
  return useMemo(() => new Map((q.data ?? []).filter(u => u.name).map(u => [u.id, u.name as string])), [q.data])
}

export type ColdroomView = 'map' | 'temps' | 'treatment'

export function ColdroomViewTabs({ view, onChange }: { view: ColdroomView; onChange: (v: ColdroomView) => void }) {
  const tab = (key: ColdroomView, label: string) => (
    <button key={key} type="button" role="tab" aria-selected={view === key} onClick={() => onChange(key)}
      className={clsx(
        'min-h-[44px] rounded-full px-4 text-caption font-semibold transition-colors',
        view === key ? 'bg-cyan-600 text-on-fill' : 'bg-surface text-fg-muted hover:bg-raised hover:text-fg-secondary')}>
      {label}
    </button>
  )
  return (
    <div role="tablist" aria-label="Coldroom views" className="flex flex-wrap gap-1.5 px-4 py-2 border-b hairline">
      {tab('map', 'Map')}{tab('temps', 'Temperatures')}{tab('treatment', 'Cold treatment (USA)')}
    </div>
  )
}

function RangeChip({ reading, room }: { reading: TempReading; room: TempRoom | undefined }) {
  if (!room) return null
  const st = rangeStatus(reading.air_temp_c, room.temp_min_c, room.temp_max_c)
  if (st !== 'too_cold' && st !== 'too_warm') return null
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-900/30 text-warning text-caption font-semibold px-2 py-0.5">
      <AlertTriangle size={12} aria-hidden="true" /> Out of range · {st === 'too_warm' ? 'too warm' : 'too cold'}
    </span>
  )
}

/** The shared fields of a new reading and of a correction. */
function ReadingFields({ air, pulp, note, setAir, setPulp, setNote, room, autoFocus }: {
  air: string; pulp: string; note: string
  setAir: (v: string) => void; setPulp: (v: string) => void; setNote: (v: string) => void
  room: TempRoom | undefined; autoFocus?: boolean
}) {
  const airN = parseTemp(air)
  const live = room && airN != null ? rangeStatus(airN, room.temp_min_c, room.temp_max_c) : null
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-caption uppercase tracking-wider text-fg-muted">Air °C</span>
          <input type="text" inputMode="decimal" value={air} autoFocus={autoFocus} aria-label="Air temperature in degrees C"
            onChange={e => setAir(sanitizeTempInput(e.target.value))} placeholder="e.g. 3.2"
            className="field text-base min-h-[44px] mt-0.5 tabular-nums" />
        </label>
        <label className="block">
          <span className="text-caption uppercase tracking-wider text-fg-muted">Pulp °C (optional)</span>
          <input type="text" inputMode="decimal" value={pulp} aria-label="Pulp temperature in degrees C"
            onChange={e => setPulp(sanitizeTempInput(e.target.value))} placeholder="optional"
            className="field text-base min-h-[44px] mt-0.5 tabular-nums" />
        </label>
      </div>
      {room && (
        <p className="text-caption text-fg-muted" data-testid="range-hint">
          {hasRange(room)
            ? <>Range {rangeText(room.temp_min_c, room.temp_max_c)}</>
            : 'No range set — set it in room setup (Config → Storage → Coldrooms).'}
          {live === 'too_warm' && <span className="text-warning font-semibold"> · Out of range: too warm</span>}
          {live === 'too_cold' && <span className="text-warning font-semibold"> · Out of range: too cold</span>}
        </p>
      )}
      <label className="block">
        <span className="text-caption uppercase tracking-wider text-fg-muted">Note (optional)</span>
        <input value={note} maxLength={500} onChange={e => setNote(e.target.value)}
          placeholder="e.g. door open during loading" className="field text-base min-h-[44px] mt-0.5" />
      </label>
    </div>
  )
}

/** Validates the typed fields; returns the row values or the sentence to show. */
function readingFromInputs(air: string, pulp: string, note: string):
  { ok: true; air: number; pulp: number | null; note: string | null } | { ok: false; error: string } {
  const a = parseTemp(air)
  if (a == null) return { ok: false, error: 'Type the air temperature, for example 3.2' }
  const p = pulp.trim() === '' ? null : parseTemp(pulp)
  if (pulp.trim() !== '' && p == null) return { ok: false, error: 'Pulp temperature must be a number, or blank' }
  if (!tempInLimits(a) || (p != null && !tempInLimits(p))) {
    return { ok: false, error: `Temperatures must be between ${TEMP_MIN_LIMIT} and ${TEMP_MAX_LIMIT} °C` }
  }
  return { ok: true, air: a, pulp: p, note: note.trim() || null }
}

function ReadingValues({ r, old, room }: { r: TempReading; old: boolean; room: TempRoom | undefined }) {
  return (
    <>
      <span className="text-caption font-semibold text-fg tabular-nums">{sastHM(r.read_at)}</span>
      <span className="text-caption text-fg-secondary">{room?.code ?? '—'}</span>
      <span className={clsx('text-sm font-bold tabular-nums', old && 'line-through text-fg-muted')}>
        {fmtTemp(r.air_temp_c)} °C
      </span>
      {r.pulp_temp_c != null && (
        <span className={clsx('text-caption tabular-nums', old ? 'line-through text-fg-muted' : 'text-fg-secondary')}>
          pulp {fmtTemp(r.pulp_temp_c)} °C
        </span>
      )}
    </>
  )
}

function ReadingNotes({ r, old, fix, by }: { r: TempReading; old: boolean; fix: TempReading | undefined; by: string | undefined }) {
  const fixNote = fix?.note ? ` · ${fix.note}` : ''
  return (
    <>
      {old && fix && <span className="text-caption text-fg-muted">Corrected to {fmtTemp(fix.air_temp_c)} °C{fixNote}</span>}
      {r.supersedes_id && <span className="text-caption text-fg-muted">Correction</span>}
      {r.note && !old && <span className="text-caption text-fg-muted truncate max-w-full">{r.note}</span>}
      {by && <span className="text-caption text-fg-muted">by {by}</span>}
    </>
  )
}

function ReadingRow({ r, old, fix, by, room, canCorrect, onCorrect }: {
  r: TempReading; old: boolean; fix: TempReading | undefined; by: string | undefined
  room: TempRoom | undefined; canCorrect: boolean; onCorrect: () => void
}) {
  return (
    <li className="card px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1">
      <ReadingValues r={r} old={old} room={room} />
      {!old && <RangeChip reading={r} room={room} />}
      <ReadingNotes r={r} old={old} fix={fix} by={by} />
      {canCorrect && !old && (
        <button type="button" onClick={onCorrect}
          aria-label={`Correct the ${sastHM(r.read_at)} reading in ${room?.code ?? 'the room'}`}
          className="ml-auto text-caption text-cyan-400 hover:text-cyan-300 min-h-[44px] px-2.5 flex items-center rounded-lg hover:bg-cyan-900/20">
          Correct
        </button>
      )}
    </li>
  )
}

type SaveVars = { coldroomId: string; air: number; pulp: number | null; note: string | null; supersedes?: TempReading }
type TempQuery = UseQueryResult<unknown>

/** Up to this many active rooms the Room picker is chips; more keep the select. */
const ROOM_CHIP_MAX = 6

/** Rooms with a window missing or running now: rooms with a missed window
 *  first, then rooms whose window is running, each in grid order. Only
 *  meaningful for today. */
function dueRoomIds(grid: GridRow[]): string[] {
  const missing = grid.filter(g => g.missing > 0)
  const due = grid.filter(g => g.missing === 0 && g.slots.some(s => s.state === 'due'))
  return [...missing, ...due].map(g => g.room.coldroom_id)
}

function RoomChips({ rooms, value, onPick, grid, ready }: {
  rooms: TempRoom[]; value: string; onPick: (id: string) => void; grid: GridRow[]; ready: boolean
}) {
  return (
    <div role="group" aria-label="Room" className="flex flex-wrap gap-1.5 mt-1">
      {rooms.map(r => {
        const g = grid.find(x => x.room.coldroom_id === r.coldroom_id)
        const badge = !g || !ready ? null : g.missing > 0 ? `${g.missing} missing` : g.slots.some(s => s.state === 'due') ? 'Due now' : null
        const on = r.coldroom_id === value
        return (
          <button key={r.coldroom_id} type="button" aria-pressed={on} title={r.name}
            onClick={() => onPick(r.coldroom_id)}
            className={clsx('min-h-[44px] rounded-full px-3.5 text-caption font-semibold flex items-center gap-1.5 transition-colors',
              on ? 'bg-cyan-600 text-on-fill' : 'bg-surface text-fg-muted hover:bg-raised hover:text-fg-secondary')}>
            <span>{r.code}</span>
            {badge && <span className={clsx('tabular-nums', on ? 'text-amber-200' : 'text-warning')}>{badge}</span>}
          </button>
        )
      })}
    </div>
  )
}

/** Which room the form records for: the hand-picked one, else the first due
 *  room (chosen once, when the day's readings first load, so a later poll
 *  never moves the room under a hand that is already typing), else the first. */
function useRoomChoice({ activeRooms, grid, isToday, ready, lastSaved }: {
  activeRooms: TempRoom[]; grid: GridRow[]; isToday: boolean; ready: boolean; lastSaved: string | null
}) {
  const [pickedRoom, setPickedRoom] = useState<string>('')
  const [autoRoom, setAutoRoom] = useState<string>('')
  useEffect(() => {
    if (autoRoom || !ready || !isToday) return
    const due = dueRoomIds(grid).filter(id => id !== lastSaved)
    const first = due.find(id => activeRooms.some(r => r.coldroom_id === id))
    if (first) setAutoRoom(first)
  }, [autoRoom, ready, isToday, grid, lastSaved, activeRooms])
  const has = (id: string) => activeRooms.some(r => r.coldroom_id === id)
  const roomId = has(pickedRoom) ? pickedRoom : has(autoRoom) ? autoRoom : (activeRooms[0]?.coldroom_id ?? '')
  return { roomId, setPickedRoom }
}

function RoomPicker({ activeRooms, roomId, onPick, grid, ready }: {
  activeRooms: TempRoom[]; roomId: string; onPick: (id: string) => void; grid: GridRow[]; ready: boolean
}) {
  if (activeRooms.length <= ROOM_CHIP_MAX) {
    return (
      <div>
        <span className="text-caption uppercase tracking-wider text-fg-muted">Room</span>
        <RoomChips rooms={activeRooms} value={roomId} onPick={onPick} grid={grid} ready={ready} />
      </div>
    )
  }
  return (
    <label className="block">
      <span className="text-caption uppercase tracking-wider text-fg-muted">Room</span>
      <select value={roomId} onChange={e => onPick(e.target.value)}
        className="field text-base min-h-[44px] mt-0.5">
        {activeRooms.map(r => <option key={r.coldroom_id} value={r.coldroom_id}>{r.code} · {r.name}</option>)}
      </select>
    </label>
  )
}

function RecordSection({ roomsQ, canRecord, activeRooms, rooms, pending, onSave, grid, isToday, ready, lastSaved }: {
  roomsQ: TempQuery; canRecord: boolean; activeRooms: TempRoom[]; rooms: TempRoom[]
  pending: boolean; onSave: (v: SaveVars) => void
  grid: GridRow[]; isToday: boolean; ready: boolean; lastSaved: string | null
}) {
  const { roomId, setPickedRoom } = useRoomChoice({ activeRooms, grid, isToday, ready, lastSaved })
  const [air, setAir] = useState('')
  const [pulp, setPulp] = useState('')
  const [note, setNote] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const room = rooms.find(r => r.coldroom_id === roomId)

  function submitNew() {
    const r = readingFromInputs(air, pulp, note)
    if (!r.ok) { setFormError(r.error); return }
    if (!roomId) { setFormError('Pick a room first'); return }
    setFormError(null)
    onSave({ coldroomId: roomId, air: r.air, pulp: r.pulp, note: r.note })
  }

  return (
    <section aria-label="Record a reading" className="card p-3 space-y-3">
      <h2 className="text-sm font-bold text-fg">Record a reading</h2>
      <RecordBody roomsQ={roomsQ} canRecord={canRecord} none={activeRooms.length === 0}>
        <form className="space-y-3" onSubmit={e => { e.preventDefault(); submitNew() }}>
          <RoomPicker activeRooms={activeRooms} roomId={roomId} onPick={setPickedRoom} grid={grid} ready={ready} />
          <ReadingFields air={air} pulp={pulp} note={note} setAir={setAir} setPulp={setPulp} setNote={setNote} room={room} />
          {formError && <p role="alert" className="text-caption text-danger">{formError}</p>}
          <Button type="submit" variant="primary" disabled={pending} className="min-h-[44px] w-full">
            {pending ? 'Saving…' : room ? `Save ${room.code} reading` : 'Save reading'}
          </Button>
        </form>
      </RecordBody>
    </section>
  )
}

function RecordBody({ roomsQ, canRecord, none, children }: {
  roomsQ: TempQuery; canRecord: boolean; none: boolean; children: ReactNode
}) {
  if (readState(roomsQ) !== 'usable') return <QueryStateGate query={roomsQ} noun="coldrooms" empty={null} />
  if (!canRecord) {
    return (
      <p className="text-caption text-fg-muted">
        You can read the log. Recording needs Coldroom edit access: ask your manager.
      </p>
    )
  }
  if (none) return <p className="text-caption text-fg-muted">No coldrooms are configured yet. Add one under Config → Storage → Coldrooms.</p>
  return <>{children}</>
}

function GridSection({ ymd, today, setYmd, roomsQ, readingsQ, grid }: {
  ymd: string; today: string; setYmd: (v: string) => void; roomsQ: TempQuery; readingsQ: TempQuery; grid: GridRow[]
}) {
  const failed = readState(roomsQ) !== 'usable' ? roomsQ : readState(readingsQ) !== 'usable' ? readingsQ : null
  return (
    <section aria-label="Check grid" className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-bold text-fg mr-auto">{ymd === today ? "Today's checks" : `Checks on ${ymd}`}</h2>
        <label className="text-caption text-fg-muted flex items-center gap-1.5">
          Date
          <input type="date" value={ymd} max={today} onChange={e => e.target.value && setYmd(e.target.value)}
            className="field text-sm min-h-[44px]" />
        </label>
      </div>
      {failed ? (
        <QueryStateGate query={failed} noun="temperature readings" empty={null} />
      ) : grid.length === 0 ? (
        <p className="text-caption text-fg-muted">No active room is owed a check. Set Checks a day under Config → Storage → Coldrooms.</p>
      ) : (
        <GridTable rows={grid} />
      )}
    </section>
  )
}

function useSaveReading(onDone: (v: SaveVars) => void) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (v: SaveVars) => {
      const { error } = await withTimeout(supabase.from('coldroom_temp_readings').insert({
        coldroom_id: v.coldroomId,
        // read_at and created_at are stamped by the server (trigger): the handset clock is not trusted.
        air_temp_c: v.air, pulp_temp_c: v.pulp, note: v.note,
        supersedes_id: v.supersedes?.reading_id ?? null,
      }), 'Coldroom: coldroom_temp_readings insert')
      if (error) throw error
    },
    onSuccess: (_d, v) => {
      onDone(v)
      void qc.invalidateQueries({ queryKey: ['coldroom-temps'] })
    },
  })
}

function useDayReadings(ymd: string, today: string) {
  const day = sastDayRange(ymd)
  const readingsQ = useQuery({
    queryKey: ['coldroom-temps', ymd],
    queryFn: () => fetchTempReadings(day.from, day.to),
    refetchInterval: ymd === today ? POLL.standard : false,
  })
  const readings = useMemo(() => readingsQ.data ?? [], [readingsQ.data])
  const corrected = useMemo(() => supersededIds(readings), [readings])
  const correctionOf = useMemo(
    () => new Map(readings.filter(r => r.supersedes_id).map(r => [r.supersedes_id as string, r])), [readings])
  const lineRows = useMemo(() => [...readings].sort((a, b) => Date.parse(b.read_at) - Date.parse(a.read_at)), [readings])
  return { readingsQ, readings, corrected, correctionOf, lineRows }
}

export default function ColdroomTemperatures({ tabs }: { tabs: ReactNode }) {
  const { can } = usePermissions()
  const canRecord = can('coldroom', 'edit')
  const nowMs = useMinuteClock()
  const today = sastTodayYMD()
  const [picked, setYmd] = useState<string | null>(null)
  const ymd = picked ?? today // follows midnight until the operator picks a day
  const [correcting, setCorrecting] = useState<TempReading | null>(null)
  const [savedCount, setSavedCount] = useState(0)
  const [lastSaved, setLastSaved] = useState<string | null>(null)

  const roomsQ = useQuery({ queryKey: TEMP_ROOMS_KEY, queryFn: fetchTempRooms })
  const rooms = useMemo(() => roomsQ.data ?? [], [roomsQ.data])
  const { readingsQ, readings, corrected, correctionOf, lineRows } = useDayReadings(ymd, today)
  const names = useRecorderNames(readings)

  const activeRooms = rooms.filter(r => r.is_active)
  const roomById = useMemo(() => new Map(rooms.map(r => [r.coldroom_id, r])), [rooms])
  const grid = useMemo(() => buildGrid(rooms, readings, ymd, nowMs), [rooms, readings, ymd, nowMs])
  const save = useSaveReading(v => { setSavedCount(n => n + 1); setLastSaved(v.coldroomId); setCorrecting(null) })
  const printHref = `/coldroom/temperatures/log?from=${shiftYmd(ymd, -6)}&to=${ymd}`

  return (
    <div className="min-h-full bg-canvas flex flex-col">
      <PageHeader title="Coldroom" icon={<Snowflake size={18} />} subtitle="Temperatures"
        actions={
          <Link to={printHref}
            className="shrink-0 flex items-center gap-1.5 min-h-[44px] rounded-lg bg-surface hover:bg-raised text-fg text-caption font-semibold px-3">
            <Printer size={14} /> Print log
          </Link>
        } />
      {tabs}
      <SubmitOverlay isPending={save.isPending} isSuccess={save.isSuccess} isError={save.isError} error={save.error}
        successLabel="Reading saved" onDismiss={() => save.reset()} />

      <div className="p-4 space-y-4 max-w-3xl w-full mx-auto">
        <RecordSection key={savedCount} roomsQ={roomsQ} canRecord={canRecord} activeRooms={activeRooms} rooms={rooms}
          pending={save.isPending} onSave={v => save.mutate(v)} grid={grid} isToday={ymd === today}
          ready={readState(roomsQ) === 'usable' && readState(readingsQ) === 'usable'} lastSaved={lastSaved} />
        <GridSection ymd={ymd} today={today} setYmd={setYmd} roomsQ={roomsQ} readingsQ={readingsQ} grid={grid} />
        <ReadingsSection readingsQ={readingsQ} ymd={ymd} lineRows={lineRows} corrected={corrected} correctionOf={correctionOf}
          names={names} roomById={roomById} canRecord={canRecord} onCorrect={setCorrecting} />
      </div>

      <CorrectionDialog correcting={correcting} roomById={roomById} isPending={save.isPending}
        onClose={() => setCorrecting(null)} onSave={save.mutate} />
    </div>
  )
}

function CorrectionDialog({ correcting, roomById, isPending, onClose, onSave }: {
  correcting: TempReading | null; roomById: Map<string, TempRoom>; isPending: boolean
  onClose: () => void; onSave: (v: SaveVars) => void
}) {
  if (!correcting) return null
  return (
    <CorrectModal reading={correcting} room={roomById.get(correcting.coldroom_id)} isPending={isPending}
      onClose={onClose}
      onSave={(v) => onSave({ coldroomId: correcting.coldroom_id, supersedes: correcting, ...v })} />
  )
}

function ReadingsSection({ readingsQ, ymd, lineRows, corrected, correctionOf, names, roomById, canRecord, onCorrect }: {
  readingsQ: TempQuery; ymd: string; lineRows: TempReading[]; corrected: Set<string>
  correctionOf: Map<string, TempReading>; names: Map<string, string>; roomById: Map<string, TempRoom>
  canRecord: boolean; onCorrect: (r: TempReading) => void
}) {
  let body: ReactNode
  if (readState(readingsQ) !== 'usable') body = <QueryStateGate query={readingsQ} noun="temperature readings" empty={null} />
  else if (lineRows.length === 0) body = <p className="text-caption text-fg-muted">No readings were recorded on {ymd}.</p>
  else {
    body = (
      <ul className="space-y-1.5">
        {lineRows.map(r => (
          <ReadingRow key={r.reading_id} r={r} old={corrected.has(r.reading_id)} fix={correctionOf.get(r.reading_id)}
            by={names.get(r.recorded_by)} room={roomById.get(r.coldroom_id)} canCorrect={canRecord}
            onCorrect={() => onCorrect(r)} />
        ))}
      </ul>
    )
  }
  return (
    <section aria-label="Readings" className="space-y-2">
      <h2 className="text-sm font-bold text-fg">Readings</h2>
      {body}
    </section>
  )
}

function CorrectModal({ reading, room, isPending, onClose, onSave }: {
  reading: TempReading; room: TempRoom | undefined; isPending: boolean
  onClose: () => void; onSave: (v: { air: number; pulp: number | null; note: string | null }) => void
}) {
  const [air, setAir] = useState(String(reading.air_temp_c))
  const [pulp, setPulp] = useState(reading.pulp_temp_c == null ? '' : String(reading.pulp_temp_c))
  const [note, setNote] = useState('')
  const [err, setErr] = useState<string | null>(null)
  return (
    <Modal title="Correct reading" subtitle={`${room?.code ?? ''} · ${sastHM(reading.read_at)} · was ${fmtTemp(reading.air_temp_c)} °C`}
      onClose={onClose} snapshot={`${air}|${pulp}|${note}`} isPending={isPending} submitLabel="Save correction"
      onSubmit={() => {
        const r = readingFromInputs(air, pulp, note)
        if (!r.ok) { setErr(r.error); return }
        onSave({ air: r.air, pulp: r.pulp, note: r.note })
      }}>
      <p className="text-caption text-fg-muted mb-2">
        The old reading stays on the log, struck through, with this one beside it. Nothing is deleted.
      </p>
      <ReadingFields air={air} pulp={pulp} note={note} setAir={setAir} setPulp={setPulp} setNote={setNote} room={room} autoFocus />
      {err && <p role="alert" className="text-caption text-danger mt-2">{err}</p>}
    </Modal>
  )
}

const SLOT_STYLE = {
  done:    'text-success',
  missing: 'text-warning font-semibold',
  due:     'text-fg-secondary',
  later:   'text-fg-muted',
} as const

function GridTable({ rows }: { rows: ReturnType<typeof buildGrid> }) {
  return (
    <div className="space-y-1.5">
      {rows.map(g => (
        <div key={g.room.coldroom_id} className="card px-3 py-2">
          <p className="text-caption font-semibold text-fg flex flex-wrap items-center gap-x-2">
            <span>{g.room.code}</span>
            <span className="text-fg-muted font-normal truncate">{g.room.name}</span>
            {g.missing > 0 && <span className="text-warning">{g.missing} missing</span>}
          </p>
          <ul className="mt-1 grid gap-1.5" style={{ gridTemplateColumns: `repeat(${Math.min(g.slots.length, 4)}, minmax(0, 1fr))` }}>
            {g.slots.map(s => (
              <li key={s.index} className="rounded-lg bg-canvas/60 px-2 py-1.5">
                <p className="text-caption text-fg-muted tabular-nums">{s.label}</p>
                <p className={clsx('text-caption tabular-nums', SLOT_STYLE[s.state])}>
                  {s.state === 'done' && s.reading
                    ? <>{fmtTemp(s.reading.air_temp_c)} °C{' '}
                        {rangeStatus(s.reading.air_temp_c, g.room.temp_min_c, g.room.temp_max_c) === 'too_warm' ||
                         rangeStatus(s.reading.air_temp_c, g.room.temp_min_c, g.room.temp_max_c) === 'too_cold'
                          ? <span className="text-warning">· Out of range</span> : null}</>
                    : s.state === 'missing' ? 'Missing'
                    : s.state === 'due' ? 'Due now'
                    : 'Not due yet'}
                </p>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}
